import React, { useState, useRef, useEffect, useCallback } from 'react'
import { arrayOf, func, shape } from 'prop-types'
import { DialogOverlay, TextField, Paragraph, useOverlay, useContainerWidth, BillboardButton, Row } from '@fs/zion-ui'
import { MenuSearch } from '@fs/zion-icon'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import ZionForm, { useZionForm } from '@fs/zion-form'
import { trackEvent } from '@fs/zion-analytics'
import { useFeatureFlag } from '@fs/zion-flags'
import { expandTileData, useAamDispatch, useAamState, ACTIONS } from '../../state/AamStore'
import useDSApplicationMode from '../../../../shared/hooks/useDSApplicationMode'
import YearPickerAncestors from './YearPickerAncestors/YearPickerAncestors'
import MissingBirthDateDialog from '../MissingBirthDateDialog/MissingBirthDateDialog'
import { newAncestorTemplate } from '../../../../shared/helpers/person_Helper'
import { getTileData } from '../../services/services'
import { getDisplayName } from '../../../../shared/helpers/name_formatting'
import sanitizeInputToDisplayName from '../../../../shared/helpers/sanitize_input'

const yearInputStyle = css`
  display: flex;
  align-items: center;
  z-index: 4;
  justify-content: stretch;
  width: 200px;
  margin: 0 auto;
`

const centerWrapper = css`
  display: flex;
  justify-content: center;
`

const centerDescription = css`
  margin-top: 20px;
  max-width: 460px;
  text-align: center;
`
const buttonWrapper = css`
  display: flex;
  justify-content: center;
`
const discoveryCenterPadding = css`
  padding: 40px;
`

export default function YearPicker({ overlay, tileData, showAncestorList }) {
  const [t] = useTranslation()
  const dispatch = useAamDispatch()
  const aamState = useAamState()
  const atWidth = useContainerWidth()

  const inWebMode = useDSApplicationMode() === 'WEB'
  const inputRef = useRef()

  const [fNameQuery, setFNameQuery] = useState('')
  const [lNameQuery, setLNameQuery] = useState('')
  const [yearQuery, setYearQuery] = useState('')
  const missingBirthDateOverlay = useOverlay()
  const [activePerson, setActivePerson] = useState()
  const [submitEnabled, setSubmitEnabled] = useState()

  const aamCustomNameFlag = useFeatureFlag('discovery_aam_custom_name')

  const yearPickerStyles = css`
    width: ${atWidth({ default: '100%' })};
  `

  const textBoxRow = css`
    display: flex;
    flex-direction: ${atWidth({ lg: 'row', default: 'column' })};
    justify-content: center;
  `

  const checkForDuplicates = (person) => {
    const ancestorTiles = tileData.filter((tile) => tile?.type === 'ancestorDetails')
    const foundDuplicate = ancestorTiles.some((personTile) => personTile?.header !== person?.header)
    return foundDuplicate
  }

  const mapAncestorsToNewYear = (yearSet, region, language) => {
    const ancestorTiles = []
    aamState?.tree?.forEach((person) => {
      if (person?.lifespan === 'Living') return
      const [birthString, deathString] = person && person.lifespan ? person.lifespan.split('-') : []
      if (deathString === 'Living') return
      const yearInt = Number.parseInt(yearSet, 10)
      const birthYear = Number.parseInt(birthString, 10)
      const deathYear = Number.parseInt(deathString, 10)
      const isBirth = birthYear === yearInt
      const isDeath = deathYear === yearInt
      if ((isBirth || isDeath) && !checkForDuplicates(person)) {
        ancestorTiles.push(
          newAncestorTemplate(
            yearSet,
            person,
            region,
            language,
            getDisplayName(person, aamState?.showNamesInRomanScript),
            isBirth ? t('aam.year-picker.ancestor-birth') : t('aam.year-picker.ancestor-death')
          )
        )
      }
    })

    return ancestorTiles
  }
  const resetMatchingAncestors = (yearSet, resetTileData) => {
    // Need traverse the array backwards while removing items so the indexes don't get out of place
    let tileIndex = resetTileData?.length
    while (tileIndex--) {
      if (resetTileData[tileIndex]?.type === 'ancestorDetails' && resetTileData[tileIndex]?.year !== yearSet) {
        resetTileData.splice(tileIndex, 1)
      }
    }
    return resetTileData
  }

  const includeAncestors = (yearSet, region, language, tilesWithAncestors) => {
    tilesWithAncestors = resetMatchingAncestors(yearSet, tilesWithAncestors)
    const ancestorYearMatches = mapAncestorsToNewYear(yearSet, region, language)

    if (ancestorYearMatches.length !== 0) {
      ancestorYearMatches.forEach((match) => tilesWithAncestors.push(match))
    }
    return tilesWithAncestors
  }

  const submitInfo = async (year, fName, lName, person = aamState.person) => {
    person.name = sanitizeInputToDisplayName(fName || person.name)
    person.surname = sanitizeInputToDisplayName(lName || person.surname)
    const displayName = getDisplayName(person, aamState?.showNamesInRomanScript)
    const atn = person?.atn
    const { region, language } = tileData[0]
    let fetchedTiles = null
    fetchedTiles = await getTileData({ countryCode: region, year: year || aamState.year })

    const expandParams = {
      tileData: includeAncestors(year, region, language, fetchedTiles?.data ?? tileData),
      person,
      atn,
      year: year || aamState.year,
      showRoman: aamState?.showNamesInRomanScript,
    }

    const tileDataRefresh = expandTileData(expandParams)

    dispatch({
      type: ACTIONS.UPDATE_TILE_DATA,
      data: {
        tileData: tileDataRefresh,
        year: year || aamState.year,
        displayName,
        fullName: displayName,
        firstName: person?.name ?? aamState?.firstName,
        lastName: person?.surname ?? aamState?.lastName,
        names: person?.names,
      },
    })

    overlay.close()
  }

  const validYearCheck = useCallback(() => {
    if (
      (yearQuery.length === 4 && yearQuery >= 1900 && yearQuery <= new Date().getFullYear()) ||
      yearQuery.length === 0
    ) {
      if (yearQuery.length === 0) {
        return null
      }
      return ''
    }
    if (yearQuery < 1900 && yearQuery.length === 4) {
      return t('aam.year-picker.error.birth-year-before-1900', 'Please enter a birth year after 1900')
    }
    return t('aam.year-picker.error.birth-year-invalid', 'Please enter a valid birth year')
  }, [t, yearQuery])

  useEffect(() => {
    const validYear = validYearCheck()
    if (fNameQuery || lNameQuery || validYear === '') {
      setSubmitEnabled(true)
    } else {
      setSubmitEnabled(false)
    }
  }, [fNameQuery, lNameQuery, validYearCheck])

  const handleYearQueryUpdate = (e) => {
    setYearQuery(e.target.value)
    validYearCheck()
  }

  const personTileClickHandler = (person) => {
    trackEvent({ event_name: 'YearPicker:SELECT_PERSON_TILE:CLICK' })
    if (!person.dob || person.dob === 'Living') {
      setActivePerson(person)
      missingBirthDateOverlay.handleClick()
      return
    }

    submitInfo(Number.parseInt(person.dob, 10), person.name, person.surname, person)
  }

  const methods = useZionForm({
    mode: 'all',
    defaultValues: { year: '' },
  })

  return (
    <>
      <DialogOverlay
        as={ZionForm}
        {...overlay}
        methods={methods}
        size="xxl"
        title={t('aam.year-picker.view-year', 'View A Different Year')}
        onSubmit={() => {
          submitInfo(yearQuery, fNameQuery, lNameQuery, aamState.person)
        }}
      >
        <div css={[!inWebMode ? discoveryCenterPadding : '', yearPickerStyles]}>
          {aamCustomNameFlag.isOn ? (
            <Row css={textBoxRow} guttersY="none">
              <div css={yearInputStyle}>
                <TextField
                  Icon={MenuSearch}
                  value={fNameQuery}
                  role="textbox"
                  label={t('aam.year-picker.enter-first-name', 'Enter a given name')}
                  name="fName"
                  data-testid="fNameInput"
                  onChange={(e) => setFNameQuery(e.target.value)}
                />
              </div>
              <div css={yearInputStyle}>
                <TextField
                  Icon={MenuSearch}
                  value={lNameQuery}
                  label={t('aam.year-picker.enter-last-name', 'Enter a surname name')}
                  name="lName"
                  data-testid="lNameInput"
                  onChange={(e) => setLNameQuery(e.target.value)}
                />
              </div>{' '}
              <div css={yearInputStyle}>
                <TextField
                  Icon={MenuSearch}
                  value={yearQuery}
                  label={t('aam.year-picker.enter-year', 'Enter a new year')}
                  type="number"
                  name="year"
                  data-testid="yearInput"
                  ref={inputRef}
                  onChange={(e) => handleYearQueryUpdate(e)}
                  warningValidate={validYearCheck}
                />
              </div>
            </Row>
          ) : (
            <div css={yearInputStyle}>
              <TextField
                Icon={MenuSearch}
                value={yearQuery}
                label={t('aam.year-picker.enter-year', 'Enter a new year')}
                type="number"
                name="year"
                data-testid="yearInput"
                ref={inputRef}
                onChange={(e) => handleYearQueryUpdate(e)}
                warningValidate={validYearCheck}
              />
            </div>
          )}

          <div css={buttonWrapper}>
            <BillboardButton
              type="submit"
              external="false"
              linkName="PersonPicker:PERSON_ENTRY_SUBMIT:CLICK"
              data-testid="submit"
              name="submit"
              color="blue"
              disabled={!submitEnabled}
            >
              {t('common-ui:submit.action', 'Submit')}
            </BillboardButton>
          </div>
          <div css={centerWrapper}>
            <div css={centerDescription}>
              <Paragraph>{t('aam.year-picker.description')}</Paragraph>
            </div>
          </div>
          {showAncestorList && <YearPickerAncestors personTileClickHandler={personTileClickHandler} />}
        </div>
      </DialogOverlay>
      {activePerson && (
        <MissingBirthDateDialog
          onUpdate={(person) => personTileClickHandler(person)}
          overlay={missingBirthDateOverlay}
          person={activePerson}
        />
      )}
    </>
  )
}

YearPicker.propTypes = {
  overlay: shape({
    close: func,
  }).isRequired,
  tileData: arrayOf(shape({})),
}
